import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';

const UgcNetMockPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleStart = () => user ? navigate('/test-zone') : navigate('/login');

  const syllabus = ['Teaching Aptitude', 'Research Aptitude', 'Comprehension', 'Communication', 'Logical & Analytical Reasoning', 'Data Interpretation', 'Information & Communication Technology (ICT)', 'People, Development & Environment', 'Higher Education System'];

  const faqs = [
    { q: 'Is UGC NET Paper 1 mock test free?', a: 'Yes, you can attempt the mock test for free with basic access.' },
    { q: 'Are the questions based on the latest syllabus?', a: 'Yes, all tests follow the latest UGC NET Paper 1 syllabus by NTA.' },
    { q: 'Will I get performance analysis after the test?', a: 'Yes, instant score and detailed analysis are provided.' },
    { q: 'Do I need to download anything?', a: 'No, the test is available online in a simulator — no download required.' },
    { q: 'Can I attempt multiple mock tests?', a: 'Yes, you can practice multiple tests to continuously improve your preparation.' },
  ];

  return (
    <>
        <Helmet>
          <title>UGC NET Paper 1 Mock Test Free | Practice Online with Score & Analysis</title>
          <meta name="description" content="Attempt UGC NET Paper 1 mock test free online. Get instant score, performance analysis & improve your preparation with real exam-level questions." />
          <meta name="keywords" content="UGC NET paper 1 mock test free, UGC NET mock test, NET JRF mock test, UGC NET practice test, NTA NET mock test" />
          <link rel="canonical" href="https://besst.in/ugc-net-mock" />
          <meta property="og:title" content="UGC NET Paper 1 Mock Test Free | Practice Online with Score & Analysis" />
          <meta property="og:description" content="Attempt UGC NET Paper 1 mock test free online. Get instant score, performance analysis & improve your preparation." />
          <meta property="og:url" content="https://besst.in/ugc-net-mock" />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="BESST" />
        </Helmet>
      <div className="min-h-screen pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="text-center max-w-4xl mx-auto">
            <div className="hero-badge mx-auto mb-6">📝 UGC NET Preparation</div>
            <h1 className="text-4xl lg:text-5xl font-extrabold gradient-text mb-6 leading-tight">UGC NET Paper 1 Mock Test Free</h1>
            <p className="text-xl text-text-muted mb-4">Practice Online with Real Exam Experience — Based on Latest NTA Syllabus</p>
            <p className="text-text-muted mb-8">✅ No Download Required &nbsp;•&nbsp; ⚡ Instant Access &nbsp;•&nbsp; 📊 Latest Pattern</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleStart} className="btn-primary px-8 py-4 text-lg font-bold rounded-xl">🚀 Start Paper 1 Mock Test</button>
              <Link to="/register" className="px-8 py-4 text-lg font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">Register & Begin Now</Link>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-3xl font-bold gradient-text text-center mb-10">Why Choose Our UGC NET Paper 1 Mock Test?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { icon: '🎯', title: 'Real Exam-Level Questions', desc: 'Aligned with UGC NET Paper 1 pattern' },
              { icon: '📚', title: 'Complete Syllabus Coverage', desc: 'Covers all 9 important units' },
              { icon: '⚡', title: 'Instant Score & Analysis', desc: 'Identify strengths & weak areas' },
              { icon: '⏱️', title: 'Time-Based Practice', desc: 'Improve speed and accuracy' },
              { icon: '👨‍🏫', title: 'Expert-Curated Questions', desc: 'Prepared by experienced educators' },
              { icon: '📊', title: 'Performance Tracking', desc: 'Monitor progress across tests' },
            ].map(f => (
              <div key={f.title} className="glass-card">
                <div className="text-3xl mb-3">{f.icon}</div>
                <h3 className="text-text-light font-bold mb-1 text-sm">{f.title}</h3>
                <p className="text-text-muted text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">📚 Syllabus Coverage — All Key Topics</h2>
          <div className="flex flex-wrap gap-3 justify-center max-w-3xl mx-auto">
            {syllabus.map(s => (
              <span key={s} className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-semibold border border-primary/30">{s}</span>
            ))}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">📊 Get Detailed Performance Analysis</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {['Score breakdown', 'Accuracy percentage', 'Time spent per section', 'Weak topic identification'].map(a => (
              <div key={a} className="glass-card text-center p-4">
                <span className="text-green-400 text-2xl block mb-2">✔</span>
                <p className="text-text-muted text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-text-light mb-4">📝 What is UGC NET Paper 1 Mock Test?</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-4">UGC NET Paper 1 is a general paper designed to test teaching and research aptitude of candidates. A mock test simulates the real exam environment and helps you evaluate your preparation.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {['Practice anytime, anywhere', 'Improve speed and accuracy', 'Understand exam pattern'].map(b => (
                <div key={b} className="flex items-center gap-2 p-3 bg-bg-secondary rounded-xl text-sm">
                  <span className="text-primary">→</span><span className="text-text-light">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-2xl mx-auto text-center">
            <h2 className="text-xl font-bold text-text-light mb-6">🎯 Ideal for UGC NET Aspirants</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {['UGC NET Candidates', 'JRF Aspirants', 'Assistant Professor Candidates', 'First-Time Test Takers'].map(t => (
                <span key={t} className="px-4 py-2 bg-accent-gold/20 text-accent-gold rounded-full text-sm font-semibold">{t}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="glass-card">
                <summary className="font-semibold text-text-light cursor-pointer list-none flex justify-between items-center">{f.q} <span className="text-primary text-xl">+</span></summary>
                <p className="text-text-muted mt-3 text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="glass-card text-center max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-text-light mb-3">Boost Your UGC NET Paper 1 Score</h2>
            <p className="text-text-muted mb-2">Start your preparation today and stay ahead of the competition.</p>
            <p className="text-accent-gold text-sm mb-6">⚡ Limited free access available</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleStart} className="btn-primary px-8 py-3 font-bold rounded-xl">🚀 Take Free Mock Test Now</button>
              <Link to="/register" className="px-8 py-3 font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">Register to Unlock Full Access</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UgcNetMockPage;
